import {useFeatureIsOn} from "@growthbook/growthbook-react";
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Banner from "@khanacademy/wonder-blocks-banner";
import Button from "@khanacademy/wonder-blocks-button";
import {addStyle, View} from "@khanacademy/wonder-blocks-core";
import {MediaLayout, Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body, HeadingLarge, Title} from "@khanacademy/wonder-blocks-typography";
import {useFlagsCookie} from "@khan/data-cookies";
import {doNotTranslate, NoTrans} from "@khan/i18n";
import {mediaQueries} from "@khan/shared-styles";
import {staticUrl} from "@khan/urls";

import PageRoot from "#app-shell-package/page-root.tsx";

import {withStrings} from "./locales/interns-page/load-strings.tsx";

const StyledImg = addStyle("img");

const WhyKhan = (): React.ReactElement => {
    return (
        <View style={styles.sideBySideContainer}>
            <View>
                {/* eslint-disable-next-line jsx-a11y/alt-text -- TODO: Address a11y error */}
                <StyledImg
                    style={styles.scalingPhoto}
                    src={staticUrl(
                        "/images/about/language_map/links-background.jpg",
                    )}
                />
            </View>
            <View>
                <HeadingLarge style={styles.heading}>
                    {t`Why Intern at Khan Academy`}
                </HeadingLarge>
                <Body style={styles.whyKhanBody}>
                    {t`Khan Academy is proudly not-for-profit. That means that you'll spend your internship working with a mission-driven team of engineers dedicated to our core goal of providing a free, world-class education for anyone, anywhere. The work you do, even as an intern, will affect millions of learners worldwide.`}
                </Body>
                <Strut size={spacing.large_24} />
                <Body style={styles.whyKhanBody}>
                    {t`During your internship, you'll be assigned to one of our engineering teams based on your interests and paired with a full-time engineer on the same team, who will serve as your mentor throughout the summer. You'll work with your mentor to set goals, solve problems, and develop your intern project.`}
                </Body>
                <Strut size={spacing.large_24} />
                <Body style={styles.whyKhanBody}>
                    {t`Your intern project isn't just a demo or busywork, and it certainly won't involve getting anyone coffee. You'll spend the bulk of your summer working on a task that integrates with your team’s broader work and has a real impact on Khan learners around the world. Along the way, you'll develop your coding skills, learn more about what it's like to work as a software engineer, and build professional experience that you'll carry with you into your future career.`}
                </Body>
            </View>
        </View>
    );
};

const Quotes = (): React.ReactElement => (
    <View>
        <HeadingLarge style={styles.heading}>
            {t`Quotes from Past Interns`}
        </HeadingLarge>
        <View style={styles.sideBySideContainer}>
            <View style={styles.internQuoteContainer}>
                <Body style={styles.internQuote}>
                    <NoTrans>
                        As my first software engineering internship experience,{" "}
                        <strong>
                            {" "}
                            I feel like I couldn't have asked for a more
                            fostering environment{" "}
                        </strong>{" "}
                        … One of the most valuable pieces of being a part of
                        Khan Academy is the people that you get to be surrounded
                        by.
                    </NoTrans>
                </Body>
                <Body style={styles.internQuoteAttribution}>
                    {doNotTranslate`- Hunter L. – Classroom Team Intern, 2021`}
                </Body>
            </View>
            <View style={styles.internQuoteContainer}>
                <Body style={styles.internQuote}>
                    <NoTrans>
                        What made my Khan Academy internships really special
                        were how open everyone was to collaborating and sharing
                        their expertise.{" "}
                        <strong> I grew a lot as an engineer </strong> on the
                        two teams I worked on, but I also was able to learn
                        about other areas like design, project management and
                        learning science.
                    </NoTrans>
                </Body>

                <Body style={styles.internQuoteAttribution}>
                    {doNotTranslate`— Ioana C. – Content Platform Intern, 2017; Test Prep Intern, 2018`}
                </Body>
            </View>
            <View style={styles.internQuoteContainer}>
                <Body style={styles.internQuote}>
                    <NoTrans>
                        Interning at Khan means working with{" "}
                        <strong> kind people </strong> on a mission you can feel
                        proud about.
                    </NoTrans>
                </Body>
                <Body style={styles.internQuoteAttribution}>
                    {doNotTranslate`— Peter L. – Classroom Team Intern 2022`}
                </Body>
            </View>
            <View style={styles.internQuoteContainer}>
                <Body style={styles.internQuote}>
                    <NoTrans>
                        KA challenged me to continue to be curious outside of
                        student life. Everyone's{" "}
                        <strong>
                            {" "}
                            eagerness to learn, kindness, and willingness{" "}
                        </strong>{" "}
                        to help have changed how I contribute to industry.
                    </NoTrans>
                </Body>
                <Body style={styles.internQuoteAttribution}>
                    {doNotTranslate`— Ishani P. – Site Reliability Engineering Intern 2021; Districts Intern 2022`}
                </Body>
            </View>
            <View style={styles.internQuoteContainer}>
                <Body style={styles.internQuote}>
                    <NoTrans>
                        I really enjoyed working with my mentors and was also
                        able to hear so many{" "}
                        <strong> unique perspectives </strong> and learn so much
                        in the process. The intern events were also amazing!
                    </NoTrans>
                </Body>
                <Body style={styles.internQuoteAttribution}>
                    {doNotTranslate`— Ying L. – Learning Platform Intern 2022`}
                </Body>
            </View>
            <View style={styles.internQuoteContainer}>
                <Body style={styles.internQuote}>
                    <NoTrans>
                        I joined KA because I wanted to work on a team where
                        everyone is <strong> passionate </strong> about the
                        mission. I've gotten that, and I have been able to work
                        with{" "}
                        <strong>
                            {" "}
                            incredibly accomplished and skilled engineers{" "}
                        </strong>{" "}
                        who take the time to teach and mentor me.
                    </NoTrans>
                </Body>
                <Body style={styles.internQuoteAttribution}>
                    {doNotTranslate`— Madeleine C. – Platform Infrastructure Intern 2022`}
                </Body>
            </View>
            <View style={styles.internQuoteContainer}>
                <Body style={styles.internQuote}>
                    <NoTrans>
                        I chose KA because I've used the product since
                        elementary school, and I really wanted to{" "}
                        <strong>
                            {" "}
                            give back and help to educate other students{" "}
                        </strong>{" "}
                        from around the world. After coming here, I realized how
                        nice and supportive everyone here has been to help me
                        continue learning (yet also have a sustainable work/life
                        balance) and{" "}
                        <strong> make a difference in the world </strong>. It's
                        also been so much fun getting to know the other interns!
                    </NoTrans>
                </Body>
                <Body style={styles.internQuoteAttribution}>
                    {doNotTranslate`— Angel W. – Test Prep Intern 2022`}
                </Body>
            </View>
            <View style={styles.internQuoteContainer}>
                <Body style={styles.internQuote}>
                    <NoTrans>
                        It was amazing getting to{" "}
                        <strong>
                            {" "}
                            solve interesting problems, make an impact, and work
                            with brilliant people{" "}
                        </strong>{" "}
                        all at the same time.
                    </NoTrans>
                </Body>
                <Body style={styles.internQuoteAttribution}>
                    {doNotTranslate`— Nisha Y. - Learning Platform 2019, Growth Team Intern 2018`}
                </Body>
            </View>
        </View>
    </View>
);

const AboutTheProgram = (): React.ReactElement => (
    <View style={[styles.sideBySideContainer, styles.aboutContainer]}>
        <View>
            <HeadingLarge style={styles.heading}>
                {t`About the Program`}
            </HeadingLarge>
            <Body style={styles.aboutBody}>
                {t`Work at a mission-driven non-profit`}
            </Body>
            <Body style={styles.aboutBody}>
                {t`10-12 week summer internship`}
            </Body>
            <Body style={styles.aboutBody}>
                {t`Remote within the United States or Canada`}
            </Body>
            <Body style={styles.aboutBody}>{t`Competitive pay rate`}</Body>
            <Body style={styles.aboutBody}>
                {t`Placement on one of our engineering teams based on your interests`}
            </Body>
            <Body style={styles.aboutBody}>
                {t`Work with a full-time mentor to develop your skills`}
            </Body>
            <Body style={styles.aboutBody}>{t`Weeklong intern hackathon`}</Body>
            <Body style={styles.aboutBody}>
                {t`Fun remote social events and activities`}
            </Body>
        </View>
        <View>
            {/* eslint-disable-next-line jsx-a11y/alt-text -- TODO: Address a11y error */}
            <StyledImg
                style={[styles.scalingPhoto]}
                src={staticUrl(
                    "/images/interns/intern-careers/homepage-photos/intern-group-summer-2020.jpg",
                )}
            />
        </View>
    </View>
);

const AboutTheApplication = (): React.ReactElement => (
    <View>
        <HeadingLarge style={styles.heading}>
            {t`About the Application`}
        </HeadingLarge>
        <Body style={styles.aboutApplication}>
            {t`Applicants should be rising undergraduate juniors and seniors pursuing computer science, engineering, math, or a related field`}
        </Body>
        <Body style={styles.aboutApplication}>
            {t`Applications are typically accepted in October`}
        </Body>
    </View>
);

const InternsPage = (): React.ReactElement => {
    const featureFlag = useFeatureIsOn("intern-applications-open");
    const flagsCookieResult = useFlagsCookie("intern-applications-open");
    const flagsCookie =
        flagsCookieResult.status === "success"
            ? flagsCookieResult.value
            : undefined;

    const flag = Boolean(featureFlag || flagsCookie);

    const renderDivider = (): React.ReactNode => {
        return (
            <MediaLayout styleSheets={dividerStyleSheets}>
                {({styles}) => {
                    return <View style={styles.divider} />;
                }}
            </MediaLayout>
        );
    };

    return (
        <PageRoot
            settings={{
                title: t`Internships at Khan Academy`,
            }}
        >
            <View>
                <View style={styles.headerContainer}>
                    <Title style={{color: color.white}}>
                        {t`Khan Academy Internships`}
                    </Title>
                    <Strut size={spacing.xLarge_32} />
                    {flag ? (
                        <>
                            <Button href="/careers#openings">
                                {t`View open positions`}
                            </Button>
                            <Strut size={spacing.medium_16} />
                            <View style={{width: 450}}>
                                <Banner
                                    text="Applications close Friday, October 11, 2024 at 5pm PT."
                                    kind="info"
                                    layout="full-width"
                                />
                            </View>
                        </>
                    ) : (
                        <Button
                            href="https://emails.khanacademy.org/join/internsignup"
                            target="_blank"
                        >
                            {t`Email me when the application goes live`}
                        </Button>
                    )}
                    <Strut size={spacing.xLarge_32} />
                </View>
                <Strut size={spacing.xxxLarge_64} />
                <WhyKhan />
                <Strut size={spacing.xxxLarge_64} />
                {renderDivider()}
                <Strut size={spacing.xxxLarge_64} />
                <AboutTheProgram />
                <Strut size={spacing.xxxLarge_64} />
                {renderDivider()}
                <Strut size={spacing.xxxLarge_64} />
                <AboutTheApplication />
                <Strut size={spacing.xxxLarge_64} />
                {renderDivider()}
                <Strut size={spacing.xxxLarge_64} />
                <Quotes />
                <Strut size={spacing.xxxLarge_64} />
            </View>
        </PageRoot>
    );
};

const dividerStyleSheets = {
    mdOrLarger: StyleSheet.create({
        divider: {
            height: 1,
            width: "100%",
            backgroundColor: color.offBlack,
            margin: "0 auto",
            maxWidth: "800px",
        },
    }),
    small: StyleSheet.create({
        // Note(nisha): The following styles are used. The linter is confused
        // on the last style so we have to disable eslint.
        // eslint-disable-next-line react-native/no-unused-styles
        divider: {
            display: "none",
        },
    }),
};

const styles = StyleSheet.create({
    headerContainer: {
        height: 424,
        backgroundColor: color.darkBlue,
        justifyContent: "flex-end",
        alignItems: "center",
        background: `url(${staticUrl("/images/interns/intern-careers/homepage-photos/intern-group-summer-2022.jpeg")}) 50% 35%`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
    },
    sideBySideContainer: {
        display: "grid",
        gridTemplateColumns: "auto auto",
        width: "100%",
        margin: "auto",
        gridColumnGap: spacing.xLarge_32,
        gridRowGap: spacing.xLarge_32,

        [mediaQueries.smOrSmaller]: {
            gridTemplateColumns: "auto",
            gridColumnGap: spacing.medium_16,
            gridRowGap: spacing.medium_16,
        },
    },
    scalingPhoto: {
        objectFit: "cover",
        margin: "auto",
        width: "100%",
    },
    whyKhanBody: {
        maxWidth: "800px",
        marginLeft: 100,
        marginRight: 100,
        fontSize: 18,
        [mediaQueries.smOrSmaller]: {
            marginLeft: 25,
            marginRight: 25,
        },
    },
    internQuoteContainer: {
        marginRight: 100,
        marginLeft: 100,
        maxWidth: 600,
        [mediaQueries.smOrSmaller]: {
            marginLeft: 25,
            marginRight: 25,
        },
    },
    internQuote: {
        fontSize: 18,
    },
    internQuoteAttribution: {
        fontStyle: "italic",
        textAlign: "right",
        marginTop: 10,
    },
    aboutContainer: {
        paddingLeft: 100,
        paddingRight: 100,
        [mediaQueries.smOrSmaller]: {
            paddingLeft: 15,
            paddingRight: 15,
        },
    },
    aboutBody: {
        textAlign: "center",
        fontSize: 20,
        padding: 10,
    },
    aboutApplication: {
        fontSize: 20,
        padding: 10,
        marginLeft: 100,
        marginRight: 100,
        [mediaQueries.smOrSmaller]: {
            marginLeft: 15,
            marginRight: 15,
        },
    },
    heading: {
        color: color.offBlack,
        textAlign: "center",
        marginBottom: 20,
    },
});

export default withStrings(InternsPage);
